import React, { useState, useEffect } from 'react';
import { GiChurch } from 'react-icons/gi';
import { LuMapPin, LuPhone, LuUser, LuSchool, LuArrowLeft, LuLoader } from 'react-icons/lu';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/firebase';

export default function AidedLpSchools({ activeSection, onSelectSection }) {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchSchools() {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, 'schools'));
        const fetched = [];

        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const sName = data.schoolname || data.school_name || data.name || '';
            const sType = (data.type || '').toLowerCase();
            const sCategory = (data.category || '').toLowerCase();

            // Match Lower Primary / LP
            const isLp = sType.includes('lower') || sType.includes('lp') || sName.toUpperCase().includes(' LP ') || sName.toUpperCase().includes('LP SCHOOL');
            const isAided = sCategory === 'aided' || !sCategory || sCategory.includes('aid');

            if (isLp && isAided) {
              fetched.push({
                id: doc.id,
                name: sName.toUpperCase(),
                manager: data.manager || null,
                principal: data.principal || data.headmaster || null,
                adminOfficer: data.administrativeofficer || data.adminOfficer || null,
                phone: data.phone || null,
                address: data.address || '',
                email: data.email || null,
                website: data.website || null,
                order: typeof data.order === 'number' ? data.order : 999,
                raw: data
              });
            }
          });

          fetched.sort((a, b) => a.order - b.order);
        }

        if (isMounted) {
          setSchools(fetched);
        }
      } catch (err) {
        console.error("Error fetching aided LP schools:", err);
        if (isMounted) setSchools([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchSchools();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 flex flex-col items-center md:items-start text-center md:text-left">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full" />
          
          <button 
            onClick={() => onSelectSection('schools')}
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#842343] mb-4 hover:underline cursor-pointer"
          >
            <LuArrowLeft className="w-4 h-4" />
            <span>Back to Schools</span>
          </button>

          <h1 className="font-serif font-bold text-[#7B1E3A] text-[32px] sm:text-[48px] lg:text-[54px] uppercase tracking-[0.03em] leading-[1.05]">
            Aided Lower Primary Schools
          </h1>
          <p className="mt-4 max-w-xl text-[#7B6B66] text-[16px] sm:text-[17px] leading-[1.6]">
            Providing foundational excellence, moral grounding, and joyful primary education across Malabar through our extensive network of aided institutions.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="max-w-[1440px] mx-auto px-6 sm:px-8 mt-12 w-full">
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading LP schools...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {schools.map((school, idx) => (
              <div key={school.id || idx} className="bg-white rounded-[28px] overflow-hidden border border-[#E8DDD7] shadow-sm hover:shadow-[0_20px_50px_rgba(123,30,58,0.12)] transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1 h-full">
                
                <div className="p-7 sm:p-8 flex flex-col flex-grow bg-white">
                  <div className="w-12 h-12 rounded-2xl bg-[#FAF6F2] flex items-center justify-center text-[#7B1E3A] mb-5 shrink-0 group-hover:bg-[#7B1E3A] group-hover:text-white transition-colors duration-300 border border-[#E8DDD7]">
                    <LuSchool className="w-6 h-6" />
                  </div>
                  
                  <h2 className="font-serif font-bold text-[#7B1E3A] text-lg sm:text-xl leading-snug mb-5 min-h-[52px] flex items-start">
                    {school.name}
                  </h2>
                  
                  <div className="space-y-3.5 mb-2">
                    {school.manager && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#FAF6F2] flex items-center justify-center shrink-0 mt-0.5 border border-[#E8DDD7]">
                          <GiChurch className="w-4 h-4 text-[#D4A64A]" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Manager</p>
                          <p className="text-[#1a1715] font-semibold text-[13.5px]">{school.manager}</p>
                        </div>
                      </div>
                    )}

                    {school.principal && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#FAF6F2] flex items-center justify-center shrink-0 mt-0.5 border border-[#E8DDD7]">
                          <LuUser className="w-4 h-4 text-[#D4A64A]" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Headmaster / Principal</p>
                          <p className="text-[#1a1715] font-semibold text-[13.5px]">{school.principal}</p>
                        </div>
                      </div>
                    )}
                    
                    {school.adminOfficer && school.adminOfficer !== 'NA' && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#FAF6F2] flex items-center justify-center shrink-0 mt-0.5 border border-[#E8DDD7]">
                          <LuUser className="w-4 h-4 text-[#7B1E3A]" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Admin Officer</p>
                          <p className="text-[#1a1715] font-semibold text-[13.5px]">{school.adminOfficer}</p>
                        </div>
                      </div>
                    )}

                    {school.address && school.address !== 'NA' && (
                      <div className="flex items-start gap-3 pt-1">
                        <LuMapPin className="w-4 h-4 text-[#842343] shrink-0 mt-0.5" />
                        <p className="text-[#5F5A58] text-[13px] leading-relaxed">{school.address}</p>
                      </div>
                    )}

                    {school.phone && (
                      <div className="flex items-center gap-3 pt-1">
                        <LuPhone className="w-4 h-4 text-[#842343] shrink-0" />
                        <a href={`tel:${school.phone}`} className="text-[#1a1715] hover:text-[#842343] font-bold text-[13px] transition-colors">
                          {school.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-7 sm:p-8 pt-0">
                  <div className="pt-4 border-t border-[#F0EBE6] flex items-center justify-between">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#842343]">
                      CSI Malabar Aided LP School
                    </span>
                    {school.phone && (
                      <a
                        href={`tel:${school.phone}`}
                        className="text-xs font-bold text-[#842343] hover:underline"
                      >
                        Call School
                      </a>
                    )}
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}

      </section>
    </div>
  );
}
